import { useCallback } from "react";
import {
  AVAILABILITY_LOG_TYPE,
  COLUMN_GAP,
  COLUMN_HEIGHT,
  COLUMN_WIDTH,
  EARLIEST_TIME,
  HEIGHT_OF_ONE_HOUR,
  ONE_HOUR,
  SNAP_INTERVAL_IN_HOURS,
} from "@miplatform/constants";
import {
  createUUID,
  getDateWithAddedTime,
  getDateWithNewHours,
  getDeactivatedIntervals,
  getDurationInHours,
  getPosition,
  isSameDay,
  snapAdjusted,
  toHour,
  toSnappedTime,
} from "@miplatform/utils";
import type { DragEndEvent, Modifier } from "@dnd-kit/core";
import type {
  AvailabilityLog,
  DeactivatedIntervalsMap,
  GetColumnsParams,
  GetUpdatesParams,
  PositionedEvent,
  SchedulerColumnTuple,
  SchedulerDate,
  SchedulerEvent,
  SchedulerUpdates,
  TimeInterval,
  UserWithAvailability,
} from "../types";

export const useDayViewViewModel = ({
  date,
  intervals,
  zoom = 1,
}: UseDayViewViewModelParams): UseDayViewViewModelReturn => {
  const attachInitialPosition = (event: SchedulerEvent): PositionedEvent => ({
    ...event,
    uuid: createUUID(),
    y:
      toHour(
        toSnappedTime(
          getDateWithAddedTime(event.displayStartTime, {
            h: event.delayedTime,
          }),
          "floor"
        )
      ) * HEIGHT_OF_ONE_HOUR,
    duration: getDurationInHours(event.displayStartTime, event.displayEndTime),
  });

  const getColumnsFromData = useCallback(
    ({
      data = [],
      groupBy,
      users = [],
    }: GetColumnsParams): SchedulerColumnTuple[] => {
      return users.map((user: UserWithAvailability): SchedulerColumnTuple => {
        const key = user.id;
        const column = data.filter(
          (event: SchedulerEvent) => event[groupBy] === key
        );
        const deactivatedIntervals = getDeactivatedIntervals(
          user.availabilityLog,
          intervals,
          date
        );

        column.sort(
          (a, b) =>
            toHour(a.scheduledStartDate ?? "") -
            toHour(b.scheduledStartDate ?? "")
        );

        const deactivatedIntervalsCoordinates = user.availabilityLog
          ?.filter((log: AvailabilityLog) => {
            if (log.type !== AVAILABILITY_LOG_TYPE.INACTIVE) {
              return false;
            }
            if (!log.from || !log.to) {
              return false;
            }
            return isSameDay(log.from, date);
          })
          .map((interval: AvailabilityLog) => {
            return {
              start: toHour(interval.from) * HEIGHT_OF_ONE_HOUR,
              end: toHour(interval.to) * HEIGHT_OF_ONE_HOUR,
            };
          });
        const modifiedColumn = column.map((event) => {
          const modifiedEvent = attachInitialPosition(event);
          const startPosition = modifiedEvent.y;
          const endPosition =
            modifiedEvent.y +
            (modifiedEvent.duration + (modifiedEvent.extendedTime ?? 0)) *
              HEIGHT_OF_ONE_HOUR;
          const isOnInactiveInterval = deactivatedIntervalsCoordinates?.some(
            (interval) =>
              startPosition < interval.end && endPosition > interval.start
          );
          return { ...modifiedEvent, isOnInactiveInterval };
        });

        return [
          key,
          modifiedColumn,
          deactivatedIntervals as DeactivatedIntervalsMap,
        ];
      });
    },
    [date, intervals]
  );

  const dndSnap = (y: number): number => {
    const hour = y / HEIGHT_OF_ONE_HOUR + EARLIEST_TIME;

    return getPosition(snapAdjusted(hour, SNAP_INTERVAL_IN_HOURS * zoom));
  };

  const getUpdates = ({
    currentDate,
    duration,
    columnId,
    y,
  }: GetUpdatesParams): SchedulerUpdates => {
    const validY = Math.min(
      Math.max(dndSnap(y), 0),
      (COLUMN_HEIGHT - (duration || 1) * HEIGHT_OF_ONE_HOUR) * zoom
    );

    const time = validY / HEIGHT_OF_ONE_HOUR / zoom;
    const effectiveDuration = duration ?? 0;
    const updates: SchedulerUpdates = {
      userId: columnId,
      scheduledStartDate: getDateWithNewHours(currentDate, time).format(),
      scheduledEndDate: getDateWithAddedTime(
        getDateWithNewHours(currentDate, time + effectiveDuration),
        { s: -1 }
      ),
    };

    return updates;
  };

  const getUpdatesOnDragEnd = (
    event: DragEndEvent
  ): SchedulerUpdates | null => {
    const { active, over } = event;
    const item = active.data.current as SchedulerEvent;

    if (!over) {
      return null;
    }

    const translatedRect = active.rect.current.translated;
    if (!translatedRect) {
      return null;
    }

    const y = translatedRect.top - over.rect.top;

    if (item.fromBucket) {
      return getUpdates({
        currentDate: date?.format(),
        duration: item?.expectedDuration
          ? item.expectedDuration / ONE_HOUR
          : null,
        columnId: String(over.id),
        y,
      });
    }

    return getUpdates({
      currentDate: item.scheduledStartDate ?? "",
      duration: item.duration ?? null,
      columnId: String(over.id),
      y,
    });
  };

  const snapModifier: Modifier = (args) => {
    const { transform, active } = args;
    if (active?.data?.current?.fromBucket) {
      return transform;
    }
    return {
      ...transform,
      x: snapAdjusted(transform.x, COLUMN_WIDTH + COLUMN_GAP),
      y: dndSnap(transform.y),
    };
  };

  return {
    getColumnsFromData,
    getUpdatesOnDragEnd,
    snapModifier,
  };
};

export interface UseDayViewViewModelParams {
  date: SchedulerDate;
  intervals: TimeInterval[];
  zoom?: number;
}

export interface UseDayViewViewModelReturn {
  getColumnsFromData: (params: GetColumnsParams) => SchedulerColumnTuple[];
  getUpdatesOnDragEnd: (event: DragEndEvent) => SchedulerUpdates | null;
  snapModifier: Modifier;
}
